// External/third-party imports
import React, { memo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import _ from "lodash";
import {
  IconButton,
  Tooltip,
  DatePicker,
  TimePicker,
} from "@toddle-edu/ds-web";
import {
  CopyOutlined,
  GoogleMeetBrand,
  MicrosoftTeamsBrand,
  ZoomMeetingBrand,
} from "@toddle-edu/ds-icons";

// Component Imports
import EventParticipantList from "MyCalendar/components/EventDetails/components/EventParticipantList";
import AttachmentList from "AppComponents/Attachment/AttachmentList";

// Internal absolute imports
import {
  END_PICKER_END_TIME,
  getEndType,
  RECURRING_QUERY_TYPE_MAPPING,
  MEETING_SERVICE_OPTIONS,
} from "MyCalendar/modules/Utils";
import { getCanUserJoinMeeting } from "MyCalendar/components/EventModal/modules/utils";
import {
  getStringAutoLinks,
  getStyleStrippedText,
  replaceSlashNWithBRTag,
  copyToClipboard,
} from "Utils";
import {
  formatDate,
  getDateMomentObjectCustom,
  getDateMomentObject,
  getIncrementedDate,
  getDateTimeDifference,
  checkIsDateAfter,
} from "Utils/DateTimeHelpers";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";
import { getSubText as getRecurrenceText } from "MyCalendar/modules/Selector";

// Hook Imports
import { useI18n } from "Hooks";

// Style Imports
import classes from "./EventBodyMeeting.scss";

// Minimum allowed gap (in minutes) between meeting start and end time
const MIN_MEETING_DURATION_MINUTES = 5;

/**
 * Normalizes a time value to a Moment object.
 * Handles both Moment objects (from TimePicker onChange) and
 * 12-hour format strings like "04:00 pm" (from initial/server data).
 */
const toMoment = value =>
  getDateMomentObjectCustom(value, DATE_TIME_CONSTANTS.hh_mm_a);

/** Returns 23:59 as a Moment for the current day — used to clamp end time */
const getMaxEndMoment = () =>
  getDateMomentObjectCustom(END_PICKER_END_TIME, DATE_TIME_CONSTANTS.HH_mm);

const getBrandIcon = meetingType => {
  switch (meetingType) {
    case MEETING_SERVICE_OPTIONS.ZOOM:
      return <ZoomMeetingBrand />;
    case MEETING_SERVICE_OPTIONS.MICROSOFT:
      return <MicrosoftTeamsBrand />;
    case MEETING_SERVICE_OPTIONS.GOOGLE:
      return <GoogleMeetBrand />;
    default:
      return null;
  }
};

const getRecurrenceTextHelper = ({
  repeatEventSettings,
  startTime,
  recurrenceStart,
  academicYearEndDate,
  metadata,
  t,
}) => {
  // This is some edgecase we are not sure of, since it is used in EventDetails as well
  const weekDays =
    _.size(repeatEventSettings?.weekDays) === 1
      ? [
          getDateMomentObject(
            !_.isEmpty(repeatEventSettings) ? startTime : recurrenceStart
          )?.day(),
        ]
      : repeatEventSettings?.weekDays;

  const repeatLabelText = getRecurrenceText({
    t,
    repeatEventSettings: {
      ...repeatEventSettings,
      id: RECURRING_QUERY_TYPE_MAPPING[metadata?.recurrenceType],
      weekDays,
      repeatType: repeatEventSettings?.frequency,
      academicYearEndDate: academicYearEndDate,
      endDate: repeatEventSettings?.endDate
        ? getDateMomentObject(repeatEventSettings?.endDate).format()
        : null,
      endType: getEndType({
        repeatEventSettings,
        academicYearEndDate,
        count: repeatEventSettings?.count,
      }),
    },
    date: !_.isEmpty(repeatEventSettings) ? startTime : recurrenceStart,
    isVideoMeeting: true,
  });

  return repeatLabelText;
};

const MeetingTitleAndLink = ({
  title,
  meetingLink,
  meetingType,
  testId,
  canUserJoinMeeting,
  t,
}) => {
  const onCopyURL = () => {
    copyToClipboard(meetingLink);
  };

  const showMeetingJoiningDetails =
    !_.isEmpty(meetingLink) && canUserJoinMeeting;

  return (
    <div className={classes.rowSpacedOut}>
      <div className={classes.videoMeetingTitleLinkContainer}>
        <Tooltip tooltip={title} dsVersion="2.0">
          <h6 className={classNames(classes.eventTitle)} dir="auto">
            {title}
          </h6>
        </Tooltip>
        {showMeetingJoiningDetails ? (
          <div className={classes.meetingLinkContainer}>
            <div className={classNames("text-label", classes.meetingLink)}>
              <a href={meetingLink} target="_blank" rel="noopener noreferrer">
                {_.replace(meetingLink, /^https?:\/\//i, "")}
              </a>
            </div>
            <Tooltip tooltip={t("common:copy")} dsVersion="2.0">
              <IconButton
                icon={<CopyOutlined variant={"subtle"} />}
                onClick={onCopyURL}
                variant={"neutral"}
                type={"plain"}
                size={"small"}
                isCompact={true}
                testId={`${testId}-copylink`}
                dsVersion={"2.0"}
              />
            </Tooltip>
          </div>
        ) : null}
      </div>
      <div className={classes.meetingBrandIcon}>
        {showMeetingJoiningDetails ? getBrandIcon(meetingType) : null}
      </div>
    </div>
  );
};

MeetingTitleAndLink.propTypes = {
  title: PropTypes.string.isRequired,
  meetingLink: PropTypes.string,
  meetingType: PropTypes.string,
  testId: PropTypes.string,
  canUserJoinMeeting: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

const DateTimeFields = ({
  canEditMeeting,
  scheduleDate,
  onUpdateDateInput,
  maxStartDate,
  isCurrentAcademicYear,
  scheduleStartTime,
  onStartTimeChange,
  scheduleEndTime,
  onEndTimeChange,
  testId,
  t,
}) => {
  return (
    <div className={classes.column}>
      {/* Date */}
      <div className={classes.rowWith12TopMarginAndSpaceBetween}>
        <div className={classNames("text-label", classes.label)}>
          {t("common:date")}:
        </div>
        <div className={classNames("text-body", classes.value)}>
          {canEditMeeting ? (
            <DatePicker
              dsVersion={"2.0"}
              onChange={onUpdateDateInput}
              picker="date"
              format={DATE_TIME_CONSTANTS.DD_MMM_ddd}
              value={getDateMomentObjectCustom(
                scheduleDate,
                DATE_TIME_CONSTANTS.YYYY_MM_DD
              )}
              variant="inline"
              inputReadOnly={true}
              minDate={getDateMomentObject()}
              maxDate={maxStartDate}
              testId={testId}
              disabled={!isCurrentAcademicYear}
            >
              <div className={classes.customChipContainer}>
                {formatDate({
                  date: getDateMomentObjectCustom(
                    scheduleDate,
                    DATE_TIME_CONSTANTS.YYYY_MM_DD
                  ),
                  format: DATE_TIME_CONSTANTS.DD_MMM_ddd,
                })}
              </div>
            </DatePicker>
          ) : (
            formatDate({
              date: getDateMomentObjectCustom(
                scheduleDate,
                DATE_TIME_CONSTANTS.YYYY_MM_DD
              ),
              format: DATE_TIME_CONSTANTS.DD_MMM_ddd,
            })
          )}
        </div>
      </div>
      {/* Time */}
      <div className={classes.rowWith12TopMarginAndSpaceBetween}>
        <div className={classNames("text-label", classes.label)}>
          {t("common:time")}:
        </div>
        <div className={classes.rowWithGap}>
          {canEditMeeting ? (
            <TimePicker
              dsVersion={"2.0"}
              useNewV3={true}
              value={toMoment(scheduleStartTime)}
              onChange={onStartTimeChange}
              minutesStep={5}
              disabled={_.isEmpty(scheduleDate) || !isCurrentAcademicYear}
            >
              <div className={classes.customChipContainer}>
                <div className={classNames("text-body", classes.value)}>
                  {formatDate({
                    date: toMoment(scheduleStartTime),
                    format: DATE_TIME_CONSTANTS.hh_mm_A,
                  })}
                </div>
              </div>
            </TimePicker>
          ) : (
            <div className={classNames("text-body", classes.value)}>
              {formatDate({
                date: toMoment(scheduleStartTime),
                format: DATE_TIME_CONSTANTS.hh_mm_A,
              })}
            </div>
          )}
          <div className={classNames("text-body", classes.value)}>{"- "}</div>
          {canEditMeeting ? (
            <TimePicker
              dsVersion={"2.0"}
              useNewV3={true}
              value={toMoment(scheduleEndTime)}
              onChange={onEndTimeChange}
              minutesStep={5}
              disabled={_.isEmpty(scheduleDate) || !isCurrentAcademicYear}
            >
              <div className={classes.customChipContainer}>
                <div className={classNames("text-body", classes.value)}>
                  {formatDate({
                    date: toMoment(scheduleEndTime),
                    format: DATE_TIME_CONSTANTS.hh_mm_A,
                  })}
                </div>
              </div>
            </TimePicker>
          ) : (
            <div className={classNames("text-body", classes.value)}>
              {formatDate({
                date: toMoment(scheduleEndTime),
                format: DATE_TIME_CONSTANTS.hh_mm_A,
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

DateTimeFields.propTypes = {
  canEditMeeting: PropTypes.bool,
  scheduleDate: PropTypes.string,
  onUpdateDateInput: PropTypes.func,
  maxStartDate: PropTypes.object,
  isCurrentAcademicYear: PropTypes.bool,
  scheduleStartTime: PropTypes.string,
  onStartTimeChange: PropTypes.func,
  scheduleEndTime: PropTypes.string,
  onEndTimeChange: PropTypes.func,
  testId: PropTypes.string,
  t: PropTypes.func.isRequired,
};

const EventBodyMeeting = props => {
  const {
    testId,
    title,
    meetingLink,
    meetingType,
    meetingCode,
    meetingPasscode,
    description,
    associatedEntities,
    sharedWith,
    isStaff,
    users,
    attachments,
    repeatEventSettings,
    recurrenceStart,
    startTime,
    metadata,
    canUserEditThisMeeting,
    isStudentProfileCalendarView,
    isAssociatedEntitiesDetailsLoading,
    // Common props
    isCurrentAcademicYear,
    academicYearEndDate,
    isParent,
    // Date and Time props
    scheduleDate,
    maxStartDate,
    onUpdateDateInput,
    // Time picker props
    scheduleStartTime,
    scheduleEndTime,
    onStartTimeChange,
    onEndTimeChange,

    // loading states
    isMeetingDetailsLoading,
  } = props;

  const { t } = useI18n();

  const repeatLabelText = repeatEventSettings
    ? getRecurrenceTextHelper({
        repeatEventSettings,
        startTime,
        recurrenceStart,
        academicYearEndDate,
        metadata,
        t,
      })
    : null;

  const canEditMeeting = canUserEditThisMeeting
    ? canUserEditThisMeeting()
    : false;

  const canUserJoinMeeting = getCanUserJoinMeeting({
    isParent,
    sharedWith,
    associatedEntities,
    users,
  });

  /**
   * When start time changes, preserve the current duration between start and end.
   * If the duration is less than the minimum (5 min), enforce the minimum.
   * If the new end time goes past 23:59, clamp it to 23:59.
   * Note: End-of-day clamping takes precedence — near midnight the actual gap
   * may be shorter than MIN_MEETING_DURATION_MINUTES.
   */
  const handleStartTimeChange = useCallback(
    newStartTime => {
      const oldStart = toMoment(scheduleStartTime);
      const oldEnd = toMoment(scheduleEndTime);

      // Preserve original duration, but never less than the minimum gap
      const duration = Math.max(
        getDateTimeDifference({
          date1: oldEnd,
          date2: oldStart,
          differenceBy: "minutes",
        }),
        MIN_MEETING_DURATION_MINUTES
      );

      // Apply duration to the new start time, clamped to end-of-day
      const computedEnd = getIncrementedDate({
        date: toMoment(newStartTime),
        key: "minutes",
        incrementBy: duration,
      });
      const maxEnd = getMaxEndMoment();
      const newEnd = checkIsDateAfter({
        date1: computedEnd,
        date2: maxEnd,
        key: "minute",
      })
        ? maxEnd
        : computedEnd;

      onStartTimeChange(newStartTime, newEnd);
    },
    [scheduleStartTime, scheduleEndTime, onStartTimeChange]
  );

  /**
   * When end time changes, ensure it is at least MIN_MEETING_DURATION_MINUTES
   * after the current start time. If the selected end time is too early,
   * snap it forward to start + minimum gap.
   */
  const handleEndTimeChange = useCallback(
    newEndTime => {
      const start = toMoment(scheduleStartTime);
      const selectedEnd = toMoment(newEndTime);

      // Earliest valid end time = start + minimum gap, capped at 23:59
      const minEnd = getIncrementedDate({
        date: start,
        key: "minutes",
        incrementBy: MIN_MEETING_DURATION_MINUTES,
      });
      const maxEnd = getMaxEndMoment();
      const clampedMinEnd = checkIsDateAfter({
        date1: minEnd,
        date2: maxEnd,
        key: "minute",
      })
        ? maxEnd
        : minEnd;

      // Snap to minimum if the selected end is at or before it
      const isEndBeforeMin = !checkIsDateAfter({
        date1: selectedEnd,
        date2: clampedMinEnd,
        key: "minute",
      });
      const finalEnd = isEndBeforeMin ? clampedMinEnd : selectedEnd;

      onEndTimeChange(finalEnd);
    },
    [scheduleStartTime, onEndTimeChange]
  );

  return (
    <div className={classes.container}>
      {/* Title combined with meeting link */}
      <MeetingTitleAndLink
        title={title}
        meetingLink={meetingLink}
        meetingType={meetingType}
        testId={testId}
        canUserJoinMeeting={canUserJoinMeeting}
        t={t}
      />
      {/* Meeting Info */}
      {!_.isEmpty(meetingCode) && canUserJoinMeeting ? (
        <div className={classes.meetingInfoContainer}>
          <div className={classNames("text-body", classes.secondaryText)}>
            {`${t("calendar:meeting_id")}: ${meetingCode}`}
          </div>
          {!_.isEmpty(meetingPasscode) ? (
            <div className={classNames("text-body", classes.secondaryText)}>
              {`${t("calendar:meeting_passcode")}: ${meetingPasscode}`}
            </div>
          ) : null}
        </div>
      ) : null}
      {/* Date and Time fields */}
      <DateTimeFields
        canEditMeeting={canEditMeeting}
        scheduleDate={scheduleDate}
        onUpdateDateInput={onUpdateDateInput}
        maxStartDate={maxStartDate}
        isCurrentAcademicYear={isCurrentAcademicYear}
        scheduleStartTime={scheduleStartTime}
        onStartTimeChange={handleStartTimeChange}
        scheduleEndTime={scheduleEndTime}
        onEndTimeChange={handleEndTimeChange}
        testId={testId}
        t={t}
      />
      {/* Recurrence Text */}
      {!_.isEmpty(repeatEventSettings) ? (
        <div className={classes.rowWith12TopMarginAndSpaceBetween}>
          <div className={classNames("text-label", classes.label)}>
            {t("calendar:repeat")}:
          </div>
          <div className={classNames("text-body", classes.recurrenceText)}>
            {repeatLabelText}
          </div>
        </div>
      ) : null}
      {/* Description */}
      {description ? (
        <div className={classes.description}>
          <div
            className={classNames("text-label", classes.labelWith4BottomMargin)}
          >
            {t("common:description")}
          </div>
          <div
            className={classNames("text-body", classes.descriptionText)}
            dangerouslySetInnerHTML={{
              __html: getStyleStrippedText({
                text: getStringAutoLinks(replaceSlashNWithBRTag(description)),
                type: "SANITIZE_DANGER_HTML_PARAGRAPH",
              }),
            }}
          ></div>
        </div>
      ) : null}
      {/* Attachments */}
      {!_.isEmpty(attachments) ? (
        <div className={classes.attachments}>
          <div
            className={classNames("text-label", classes.labelWith4BottomMargin)}
          >
            {t("common:attachments")}
          </div>
          <div className={classes.attachmentValue}>
            <AttachmentList
              dsVersion="2.0"
              name="attachments"
              value={attachments}
              attachmentMode={"view"}
              cardType="design-media-card"
              mediaCardSize={"small"}
              showCreateDropdown={false}
              showDeleteForSelfInViewMode={true}
              isShowIconsInBar={true}
              showV2Icons={true}
              isPrintEnabled={true}
              mode={"view"}
              showRenameAttachmentOption={false}
              attachmentContainerStyle={{ gridGap: "8px" }}
            />
          </div>
        </div>
      ) : null}
      {/* Participant section */}
      {isStaff && !isStudentProfileCalendarView ? (
        <div className={classes.participants}>
          <EventParticipantList
            associatedEntities={associatedEntities}
            sharedWith={sharedWith}
            isStaff={isStaff}
            users={users}
            isAssociatedEntitiesDetailsLoading={
              isAssociatedEntitiesDetailsLoading
            }
            eventTitle={title}
            isEventDetailsLoading={isMeetingDetailsLoading}
          />
        </div>
      ) : null}
    </div>
  );
};

EventBodyMeeting.propTypes = {
  testId: PropTypes.string,
  title: PropTypes.string.isRequired,
  meetingLink: PropTypes.string,
  description: PropTypes.string,
  associatedEntities: PropTypes.array,
  sharedWith: PropTypes.array,
  isStaff: PropTypes.bool,
  users: PropTypes.array,
  isAssociatedEntitiesDetailsLoading: PropTypes.bool,
  // Common props
  isCurrentAcademicYear: PropTypes.bool,
  // Date and Time props
  scheduleDate: PropTypes.string,
  maxStartDate: PropTypes.object,
  onUpdateDateInput: PropTypes.func,
  // Time picker props
  scheduleStartTime: PropTypes.string,
  scheduleEndTime: PropTypes.string,
  onStartTimeChange: PropTypes.func,
  onEndTimeChange: PropTypes.func,
};

EventBodyMeeting.defaultProps = {
  testId: "",
  meetingLink: "",
  description: "",
  associatedEntities: [],
  sharedWith: [],
  isStaff: false,
  users: [],
  isAssociatedEntitiesDetailsLoading: false,
  // Common props
  isCurrentAcademicYear: true,
  // Date and Time props
  scheduleDate: "",
  maxStartDate: null,
  onUpdateDateInput: () => {},
  // Time picker props
  scheduleStartTime: "",
  scheduleEndTime: "",
  onStartTimeChange: () => {},
  onEndTimeChange: () => {},
};

export default memo(EventBodyMeeting);
